import fp, { isEmpty } from "lodash";
import moment from "moment";

import {
	DaysToDeliveryFieldsNames,
	GoogleAddressComponentsFields,
	GoogleAddressOptionFields,
	ValidationMessages,
} from "globalConstants";
import {
	checkAddressFieldsEmpty,
	checkIsLatOrLngManual,
	getObjectByPath,
	isEmptyObject,
} from "helpers";
import { TypeOfBusinessValues } from "types";

import { riskExposureCalculatorDaysToDeliverySum } from "./creditReviewValidators";
import { additionalFeeWarning } from "./warnings";

export const webAddressRegex =
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,50}(:[0-9]{1,50})?(\/.*)?$/i;

export const baseEmailTemplateRegex =
	/^[a-z\d]((\.(?!\.))|[-+\w]){0,49}@[a-z\d\-.]{1,251}\.[a-z\d-]{1,50}$/i;
export const subDomainRegex = /^[a-z\d]+([a-z\d]*-(?!$)[a-z\d]*)*$/i;

export const matchesRegex = (regex, message) => value =>
	regex.test(value) ? null : message;

export const required = value =>
	requiredWithMessage("Field is required")(value);

export const requiredNotFalsy = (value, allValues, props, name) =>
	value ? undefined : "Field is required";

export const requiredWithMessage =
	(message = "Field is required") =>
	value => {
		const valueIsEmpty = !value;
		const valueIsNotZero = value !== 0;
		const isArrayEmpty = Array.isArray(value) && !value.length;

		if ((valueIsEmpty && valueIsNotZero) || isArrayEmpty) {
			return message;
		}
		return undefined;
	};

export const requiredDropDownWithMessage =
	(message = "Field is required") =>
	value =>
		value?.value ? undefined : message;

export const requiredDropDownSplitProfile = value =>
	requiredDropDownWithMessage("Select an available Split Profile")(value);
export const requiredDropDownScheduleA = value =>
	requiredDropDownWithMessage("Select an available Schedule A ")(value);

export const isInactiveAgentOffice = value => {
	if (value?.active === false) return "Only active office can be selected";
	return undefined;
};

export const onlyActiveUser = value => {
	if (
		value?.status?.value === 1 ||
		value?.status === 1 ||
		value?.status === 4
	)
		return "Only active user can be selected";
	return undefined;
};

export const warn = values => {
	const warnings = {};

	if (
		(values.miscFeeNumberOneType === "1" ||
			values.miscFeeNumberOneType === "2") &&
		values.miscFeeNumberOnePercentage
	) {
		warnings.miscFeeNumberOnePercentage =
			"Percentage cannot be used with monthly or annual";
	}

	if (
		(values.miscFeeNumberTwoType === "1" ||
			values.miscFeeNumberTwoType === "2") &&
		values.miscFeeNumberTwoPercentage
	) {
		warnings.miscFeeNumberTwoPercentage =
			"Percentage cannot be used with monthly or annual";
	}

	if (
		(values.miscFeeNumberThreeType === "1" ||
			values.miscFeeNumberThreeType === "2") &&
		values.miscFeeNumberThreePercentage
	) {
		warnings.miscFeeNumberThreePercentage =
			"Percentage cannot be used with monthly or annual";
	}

	if (values.giftLoyalty) {
		warnings.nailSoftGiftLoyalty =
			"If selected, please add Gift / Loyalty CSV file as an attachment to this application";
	}

	warnings.tsysTokenization = additionalFeeWarning(values.tsysTokenization);

	return warnings;
};

export const matchesAddressRegex = matchesRegex(
	/^[0-9a-z!&#,.'’"()_/-\s]*$/i,
	`Only letters, digits and special characters are allowed`,
);

export const matchesCityRegex = matchesRegex(
	/^[a-z\-'’.\s]*$/i,
	`Only letters, dashes, dots, and apostrophes are allowed`,
);

export const maxLength = max => value => {
	return value && value.length > max
		? `Max length is  ${max} symbols`
		: undefined;
};

const maxLengthPhone = max => value => {
	return value && value.length > max
		? `Max length is ${max} digits`
		: undefined;
};

export const minLength = min => value => {
	return value && value.length < min
		? `Min length is ${min} symbols`
		: undefined;
};

const arrayToSentence = arr => {
	if (!arr || arr.length === 0) {
		return "";
	} else if (arr.length === 1) {
		return arr[0].toString();
	} else {
		return arr.slice(0, arr.length - 1).join(", ") + " or " + arr.slice(-1);
	}
};

export const exactLength =
	(exactValues, units = "characters", msg, canBeEmpty) =>
	value => {
		const exactValuesArr = Array.isArray(exactValues)
			? exactValues
			: [exactValues];

		if (
			(canBeEmpty && value === undefined) ||
			value === null ||
			value === ""
		) {
			return undefined;
		}

		if (!exactValuesArr.includes(value?.length)) {
			return (
				msg ||
				`Length should be ${arrayToSentence(exactValuesArr)} ${units}`
			);
		} else {
			return undefined;
		}
	};

const minLengthPhone = min => value => {
	return value && value.length < min
		? `Min length is ${min} digits`
		: undefined;
};

export const maxValueEx = (max, units) => value => {
	return value && value > max
		? `Must be not more than ${max} ${units ? units : ""}`
		: undefined;
};

export const minValueEx = (min, units) => value => {
	return value && value < min
		? `Cannot be less than ${min} ${units}`
		: undefined;
};

export const minValueMinutes = minValueEx(0, "minutes");
export const maxValueMinutes = maxValueEx(59, "minutes");

export const minValueHours = minValueEx(1, "hour");
export const maxValueHours = maxValueEx(12, "hours");
export const minValueDays = minValueEx(31, "days");
export const minValueEx1 = minValueEx(1, "");

export const maxValue = (max, msg) => value => {
	if (value && value > max) {
		return msg || `Must be not more than ${max} percent`;
	} else {
		return undefined;
	}
};

export const maxValueSign = (max, sign, msg) => value => {
	const result = parseFloat(value).toFixed(sign);
	// const maxResult = parseFloat(max).toFixed(sign);
	if (value && result > +max) {
		return msg || `Must be not more than ${max} `;
	} else {
		return undefined;
	}
};

export const rangeMinMaxValueForNumber = (min, max) => value => {
	return (value && value < min) || (value && value > max)
		? `Value must be greater than ${min} and less than ${max}`
		: undefined;
};

export const minValue = (min, msg) => value => {
	if (value && value < min) {
		return msg || `Must be at least ${min} percent`;
	} else {
		return undefined;
	}
};

export const minLengthTaxId = value => {
	return value && value.length < 10 ? `Min length is 9 symbols` : undefined;
};
export const maxLengthTaxId = value => {
	return value && value.length > 10 ? `Max length is 9 symbols` : undefined;
};

export const minLengthSSN = value => {
	return value && value.length < 11 ? `Min length is 9 symbols` : undefined;
};
export const maxLengthSSN = value => {
	return value && value.length > 11 ? `Max length is 9 symbols` : undefined;
};

export const validateUrl = value => {
	if (!value) {
		return value;
	}

	return !webAddressRegex.test(value)
		? `Incorrect website address`
		: undefined;
};

const onlyNumberPhoneMaxLength = maxLength => (value, previousValue) => {
	if (!value) {
		return value;
	}
	const onlyNums = value.replace(/[^\d]/g, "");
	if (!previousValue || value.length > previousValue.length) {
		// typing forward
		if (onlyNums.length === 3) {
			return "(" + onlyNums + ")-";
		}
		if (onlyNums.length === 6) {
			return onlyNums.slice(0, 3) + "-" + onlyNums.slice(3) + "-";
		}
		if (onlyNums.length === 9) {
			return onlyNums.slice(0, 3) + "-" + onlyNums.slice(3) + "-";
		}
	}
	if (onlyNums.length <= 3) {
		return onlyNums;
	}
	if (onlyNums.length <= 6) {
		return onlyNums.slice(0, 3) + "-" + onlyNums.slice(3);
	}
	if (onlyNums.length <= 9) {
		return onlyNums.slice(0, 3) + "-" + onlyNums.slice(3);
	}
	return (
		"(" +
		onlyNums.slice(0, 3) +
		")-" +
		onlyNums.slice(3, 6, 9) +
		"-" +
		onlyNums.slice(6, maxLength)
	);
};

export const onlyDate = (value, previousValue) => {
	if (!value) {
		return value;
	}

	const containsOtherChars = /^\d/.test(value);

	if (
		value.length === 10 &&
		value.split("/").length === 3 &&
		!containsOtherChars
	) {
		//value already formated
		return value;
	}

	if (previousValue && value.length < previousValue.length) {
		return value;
	}

	const onlyNums = value.replace(/[^\d]/g, "");

	if (!previousValue || value.length > previousValue.length) {
		// typing forward
		if (onlyNums.length === 2) {
			return onlyNums + "/";
		}
		if (onlyNums.length === 4) {
			return onlyNums.slice(0, 2) + "/" + onlyNums.slice(2) + "/";
		}
		if (onlyNums.length >= 8) {
			return (
				onlyNums.slice(0, 2) +
				"/" +
				onlyNums.slice(2, 4) +
				"/" +
				onlyNums.slice(4, 8)
			);
		}
	}
	if (onlyNums.length <= 2) {
		return onlyNums;
	}
	if (onlyNums.length <= 4) {
		return onlyNums.slice(0, 2) + "/" + onlyNums.slice(2);
	}

	return (
		onlyNums.slice(0, 2) +
		"/" +
		onlyNums.slice(2, 4) +
		"/" +
		onlyNums.slice(4, 8)
	);
};

export const onlyLowerCase = value => value && value.toString().toLowerCase();

export const emailValid = value => {
	const error = "Invalid email address";

	if (!value) return undefined;
	if (value?.length > 255) return error;
	if (!baseEmailTemplateRegex.test(value)) return error;

	const subDomains = value.split("@")[1].split(".");
	if (subDomains.length < 2) return error;

	const anySubDomainIsInvalid = subDomains.some(
		x => !x || x.length > 50 || !subDomainRegex.test(x),
	);

	if (anySubDomainIsInvalid) return error;

	let regEmail =
		/^(([^<>()[\]\\,;:\s@"]+(\.[^<>()[\]\\,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/;
	return regEmail.test(value) ? undefined : error;
};

export const onlyPercent = (value, previousValue) => {
	if (!value) {
		return value;
	}

	let valueMatch = !!value.match(/^\d{0,3}(\.\d{0,2})?$/g);

	if (!valueMatch) {
		// Handle case when first value was 1.00, then you type in 1
		// You get 1.001. It doesn't match the regex above.
		// Therefore it was returning empty strings
		if (value.match(/^\d{0,3}(\.\d{0,2})?.?$/g)) {
			return value.substring(0, value.length - 1);
		}
		return previousValue === undefined ? "" : previousValue;
	}
	const onlyNums = value.replace(/[^\d|.]/g, "");
	return onlyNums;
};

export const onlyPercentToSixFloat = (value, previousValue) => {
	if (!value) {
		return value;
	}
	const valueAfterDotString = value?.toString().split(".")[1];

	if (
		!fp.isEmpty(previousValue) &&
		previousValue?.length > 0 &&
		valueAfterDotString?.length > 6 &&
		!fp.isEmpty(valueAfterDotString)
	) {
		return previousValue;
	} else {
		let valueMatch = !!value.match(/^\d{0,3}(\.\d{0,6})?$/g);

		if (!valueMatch) {
			// Handle case when first value was 1.00, then you type in 1
			// You get 1.001. It doesn't match the regex above.
			// Therefore it was returning empty strings
			if (value.match(/^\d{0,3}(\.\d{0,6})?.?$/g)) {
				return value.substring(0, value.length - 1);
			}
			return previousValue === undefined ? "" : previousValue;
		}
		const onlyNums = value.replace(/[^\d|.]/g, "");
		return onlyNums;
	}
};

export const currency = (value, previousValue) => {
	if (!value) {
		return value;
	}

	let valueMatch = !!value.match(/^\d{0,20}(\.\d{0,2})?$/g);

	if (!valueMatch) {
		// Handle case when first value was 1.00, then you type in 1
		// You get 1.001. It doesn't match the regex above.
		// Therefore it was returning empty strings
		if (value.match(/^\d{0,20}(\.\d{0,2})?.?$/g)) {
			return value.substring(0, value.length - 1);
		}
		return previousValue === undefined ? "" : previousValue;
	}
	const onlyNums = value.replace(/^0+(?!\.|$)|[^\d|.]/g, "");
	return onlyNums;
};

export const currencySixSigns = (value, previousValue) => {
	if (!value) {
		return value;
	}

	const valueAfterDotString = value?.toString().split(".")[1];
	if (
		!fp.isEmpty(previousValue) &&
		previousValue?.length > 0 &&
		valueAfterDotString?.length > 6 &&
		!fp.isEmpty(valueAfterDotString)
	) {
		return previousValue;
	} else {
		let valueMatch = !!value.match(/^\d{0,20}(\.\d{0,6})?$/g);

		if (!valueMatch) {
			// Handle case when first value was 1.00, then you type in 1
			// You get 1.001. It doesn't match the regex above.
			// Therefore it was returning empty strings
			if (value.match(/^\d{0,20}(\.\d{0,6})?.?$/g)) {
				return value.substring(0, value.length - 1);
			}
			return previousValue === undefined ? "" : previousValue;
		}
		const onlyNums = value.replace(/^0+(?!\.|$)|[^\d|.]/g, "");
		return onlyNums;
	}
};
//TODO: combine in one 'currency' function
export const currencyFourSigns = (value, previousValue) => {
	if (!value) {
		return value;
	}

	let valueMatch = !!value.match(/^\d{0,40}(\.\d{0,4})?$/g);

	if (!valueMatch) {
		// Handle case when first value was 1.00, then you type in 1
		// You get 1.001. It doesn't match the regex above.
		// Therefore it was returning empty strings
		if (value.match(/^\d{0,40}(\.\d{0,4})?.?$/g)) {
			return value.substring(0, value.length - 1);
		}
		return previousValue === undefined ? "" : previousValue;
	}
	const onlyNums = value.replace(/^0+(?!\.|$)|[^\d|.]/g, "");
	return onlyNums;
};

export const onlyDigits = value => {
	if (!value) return value;

	return value.replace(/[^\d]/g, "");
};

export const ageDaysValidate = value => {
	if (!value) return value;

	const noSpacesNums = value.replace(/\s/g, "");

	if (value.length > 5) {
		return noSpacesNums.slice(0, -1);
	} else {
		const onlyNums = noSpacesNums.replace(/[^0-9]/, "");
		return onlyNums;
	}
};

export const ageHoursValidate = value => {
	if (!value) return value;

	const noSpacesNums = value.replace(/\s/g, "");

	if (value.length > 2 || (value.length === 2 && parseInt(value) > 23)) {
		return noSpacesNums.slice(0, -1);
	} else {
		const onlyNums = noSpacesNums.replace(/[^0-9]/, "");
		return onlyNums;
	}
};

export const ageMinutesValidate = value => {
	if (!value) return value;

	const noSpacesNums = value.replace(/\s/g, "");

	if (value.length > 2 || (value.length === 2 && parseInt(value) > 59)) {
		return noSpacesNums.slice(0, -1);
	} else {
		const onlyNums = noSpacesNums.replace(/[^0-9]/, "");
		return onlyNums;
	}
};

export function floatValidation(value) {
	if (!value) {
		return;
	}

	let valueMatch = !!value
		.toString()
		.match(/^(((-?\d{1,20})(\.\d{0,2})?)|(\.\d{1,2}))$/g);
	if (!valueMatch) {
		return "Valid number is expected";
	}
}

export function floatToSixValidation(value) {
	if (!value) {
		return;
	}

	let valueMatch = !!value
		.toString()
		.match(/^(((-?\d{1,20})(\.\d{0,6})?)|(\.\d{1,6}))$/g);
	if (!valueMatch) {
		return "Valid number is expected";
	}
}

export const digitsWithMinus = (value, previousValue) => {
	if (!value) {
		return value;
	}

	let valueMatch = !!value.match(/^-?\d{0,20}(\.\d{0,2})?$/g);
	if (!valueMatch) {
		return previousValue || "";
	}

	return value;
};

export const onlyAlphaNumericAndDash = value => {
	if (!value) {
		return undefined;
	}
	const onlyAlphaNumAndDash = value.replace(/[^a-zA-Z0-9-]/g, "");

	if (onlyAlphaNumAndDash !== value) {
		return "Allowed special characters: - (dash)";
	}

	return undefined;
};

export const validateMid = value => {
	if (!value) {
		return value;
	}
	return matchesRegex(/^([a-z]{2})?\d+$/i, "Invalid mid number")(value);
};

export const onlyLetters = value => {
	if (!value) {
		return value;
	}
	const onlyLetter = value.replace(/([^a-zA-Z\s])/g, "");
	return onlyLetter;
};

export const matchesNameRegex = matchesRegex(
	/^[a-z\-'’\s]*$/i,
	`Only letters, dash or apostrophe are allowed`,
);

export const onlyAlphanumericAndCustomSpecialCharacters = value => {
	if (!value) {
		return value;
	}
	const onlyAllowedSymbols = value.replace(
		/([^a-zA-Z0-9\s@$%*+:!#&()?,.'_/-])/g,
		"",
	);
	return onlyAllowedSymbols;
};

export const noSpace = value => {
	if (!value) {
		return value;
	}

	const noSpace = value.replace(/\s/g, "");
	return noSpace;
};

export const noSpaceAllowed = value => {
	return /\s/g.test(value) ? "Space is not allowed" : null;
};

export const alphanumericAndSpecialCharacters = value =>
	value?.replace(/([^\w\s$!@%&#,.'`"*(){}:^+?~=|;/<>[\]\\-])/g, "");

export const onlyAlphanumericAndSpecialCharacters = (value, previousValue) => {
	if (!value) return value;

	const onlyAllowedSymbols = value.replace(
		/([^a-zA-Z0-9\s!&#,.'’"()_/-])/g,
		"",
	);
	return onlyAllowedSymbols;
};

export const onlyAlphanumericAndExtraCharacters = value => {
	if (!value) return value;

	const onlyAllowedSymbols = value.replace(
		// eslint-disable-next-line no-useless-escape
		/([^a-zA-Z0-9\s!&#,.'’"+\(\)\\()_/-])/g,
		"",
	);
	return onlyAllowedSymbols;
};

export const onlyAlphanumericAndSlash = value => {
	if (!value) {
		return value;
	}
	const onlyAllowedSymbols = value.replace(/([^a-zA-Z0-9\s/])/g, "");
	return onlyAllowedSymbols;
};

export const onlyAlphanumericAndDashNormalizer = value => {
	if (!value) return value;

	return value.replace(/[^a-zA-Z0-9-]/g, "");
};

export const regExpOneSpace = value => {
	if (!value) {
		return value;
	}

	const regex = /\s(?=\s)/;
	return regex.test(value) ? `Only one space can be added` : null;
};

export const onlyMaxDigits = number => value => {
	if (!value) {
		return value;
	}
	const onlyNums = value.replace(/[^\d]/g, "");
	return `${onlyNums.slice(0, number)}`;
};

export const onlyMaxDigits2 = onlyMaxDigits(2);
export const onlyMaxDigits3 = onlyMaxDigits(3);
export const onlyMaxDigits4 = onlyMaxDigits(4);
export const onlyMaxDigits6 = onlyMaxDigits(6);
export const onlyMaxDigits7 = onlyMaxDigits(7);
export const onlyMaxDigits10 = onlyMaxDigits(10);
export const onlyMaxDigits20 = onlyMaxDigits(20);

export const exactNumberOfDigits = number => value => {
	if (!value) {
		return value;
	}
	const onlyNums = value.replace(/[^\d]/g, "");
	return `${onlyNums.slice(0, number)}`;
};

export const onlyTwoUpperCaseLetters = value => {
	if (!value) {
		return value;
	}
	const word = value.replace(/[^a-zA-Z]/g, "").toUpperCase();
	return `${word.slice(0, 2)}`;
};

export const validSubdomain = value => {
	if (!value) {
		return value;
	}
	const onlyAllowedSymbols = value.replace(
		/([^a-zA-Z0-9\s!&#,'’"()/-])/g,
		"",
	);
	return onlyAllowedSymbols;
};

// The Luhn Algorithm (mod 10).
export const validCreditCard = value => {
	if (!value) {
		return undefined;
	}

	let nCheck = 0,
		bEven = false;
	value = value.replace(/\D/g, "");

	for (var n = value.length - 1; n >= 0; n--) {
		var cDigit = value.charAt(n),
			nDigit = parseInt(cDigit, 10);

		if (bEven && (nDigit *= 2) > 9) nDigit -= 9;

		nCheck += nDigit;
		bEven = !bEven;
	}

	return nCheck && nCheck % 10 === 0
		? undefined
		: "Credit card number is invalid";
};

export const onlyMaxAlphaNumeric = number => value => {
	if (!value) {
		return value;
	}
	const onlyNums = value?.replace(/[^a-z0-9]/gi, " ");
	return `${onlyNums.slice(0, number)}`;
};

export const onlyMaxAlphaNumeric4 = onlyMaxAlphaNumeric(4);
export const onlyMaxAlphaNumeric7 = onlyMaxAlphaNumeric(7);
export const onlyMaxAlphaNumeric8 = onlyMaxAlphaNumeric(8);
export const onlyMaxAlphaNumeric14 = onlyMaxAlphaNumeric(14);
export const onlyMaxAlphaNumeric19 = onlyMaxAlphaNumeric(19);
export const onlyMaxAlphaNumeric20 = onlyMaxAlphaNumeric(20);

export const maxLength2 = maxLength(2);
export const maxLength4 = maxLength(4);
export const maxLength7 = maxLength(7);
export const maxLength8 = maxLength(8);
export const maxLength9 = maxLength(9);
export const maxLength10 = maxLength(10);
export const maxLength12 = maxLength(12);
export const maxLength16 = maxLength(16);
export const maxLength17 = maxLength(17);
export const maxLength20 = maxLength(20);
export const maxLength30 = maxLength(30);
export const maxLength50 = maxLength(50);
export const maxLength100 = maxLength(100);
export const maxLength150 = maxLength(150);
export const maxLength500 = maxLength(500);
export const maxLength1000 = maxLength(1000);
export const maxLength2000 = maxLength(2000);
export const maxLength4000 = maxLength(4000);

export const minLength1 = minLength(1);
export const minLength2 = minLength(2);
export const minLength3 = minLength(3);
export const minLength4 = minLength(4);
export const minLength5 = minLength(5);
export const minLength6 = minLength(6);
export const minLength7 = minLength(7);
export const minLength8 = minLength(8);
export const minLength9 = minLength(9);
export const minLength10 = minLength(10);
export const minLength14 = minLength(14);

export const maxValue999999999999999_9999ToFixed4 = maxValueSign(
	"999999999999999.9999",
	4,
);

export const maxValueForAuroraPosFee = maxValue(
	159.95,
	"Allowed range is from $59.95 to $159.95",
);

export const minValueForAuroraPosFee = minValue(
	59.95,
	"Allowed range is from $59.95 to $159.95",
);

export const maxValue100 = maxValue(100);
export const maxDays5 = maxValueEx(5, "days");

export const rangeMinMaxValueForNumber300and850 = rangeMinMaxValueForNumber(
	300,
	850,
);

export const maxLengthPhone24 = maxLengthPhone(24);
export const minLengthPhone14 = minLengthPhone(14);

export const onlyNumberPhone = onlyNumberPhoneMaxLength(14);
export const onlyNumberPhone20 = onlyNumberPhoneMaxLength(20);

export const exactNumberOfDigits8 = exactNumberOfDigits(8);
export const exactNumberOfDigits9 = exactNumberOfDigits(9);
export const exactLengthNumbers9 = exactLength(9, "numbers");
export const validateSwiftBic = exactLength([8, 11]);

export const exactFieldLength12 = exactLength(
	12,
	undefined,
	"Field must contain 12 chars",
	true,
);

//Business Info
export const validateHowDoYouAcceptCards = (value, allValues) => {
	let errorTitle = "Field is required";

	let swiped = parseFloat(allValues.swiped) || 0;
	let commerse = parseFloat(allValues.eCommerce) || 0;
	let keyed = parseFloat(allValues.keyed) || 0;

	let isNoneValues = swiped + commerse + keyed === 0;
	if (isNoneValues) {
		return errorTitle;
	}

	let isAddUp100 = swiped + commerse + keyed >= 100;
	if (!isAddUp100 && !value) {
		return errorTitle;
	}
};

export const validateWhenRefundPolicyIsOther = (value, allValues) => {
	if (allValues.refundPolicy !== "Other" || value) {
		return undefined;
	}
	return "Field is required";
};

export const validateWhenUseAThirdPartyForFulfillment = (value, allValues) => {
	if (allValues.doYouUseAThirdPartyForFulfillment === "false" || value) {
		return undefined;
	}
	return "Field is required";
};

export const validateWhenCreditDebitCardTransactionsSettledTypeIsOther = (
	value,
	allValues,
) => {
	if (
		allValues.creditAndDebitCardTransactionsAreSettled !== "Other" ||
		value
	) {
		return undefined;
	}
	return "Field is required";
};

export const validateWhenGiftLoyaltyEnabled = (value, allValues) => {
	if (!allValues.giftLoyalty || value) {
		return undefined;
	}
	return "Field is required";
};

export const validateWhenTypeOfBusinessIsOther = (value, allValues) => {
	if (
		+allValues.typeOfBusiness !== TypeOfBusinessValues.Other.value ||
		!isEmpty(value)
	) {
		return undefined;
	}

	return "Field is required";
};

export const validateWhenTypeOfLocationIsOther = (value, allValues) =>
	allValues?.businessAddress?.address?.type_of_location !== "other" || value
		? undefined
		: "Field is required";

export const validateWhenAmexESA = (value, allValues) => {
	if (allValues.amexType !== "Esa" || value) {
		return undefined;
	}
	return "Field is required";
};

export const dateValidation = (value, allValues) => {
	try {
		if (!value) {
			return undefined;
		}

		if (value.length !== 10) {
			return "Invalid date";
		}
		let date = moment(value, "MM/DD/YYYY");
		if (!date.isValid()) {
			return "Invalid date";
		}
	} catch (error) {
		return "Invalid date";
	}
	return undefined;
};

export const maxDateNow = value => {
	if (!value) {
		return undefined;
	}

	const enteredDate = moment(value, "MM/DD/YYYY");
	const isFuture = enteredDate.isAfter(moment());
	if (isFuture) {
		return "Date must be in the past";
	}
};

export const validateForEndDate = (value, allValues) => {
	if (!value) {
		return undefined;
	}

	let checkEndDate = moment(value).isSameOrBefore(allValues.startDate);
	if (checkEndDate) {
		return "The end date must not be less than the start date";
	}

	if (allValues.startDate.length < 1) {
		return "Cannot specify an end date without a start date";
	}
};

export const minDateYearAgo = value => {
	if (!value) {
		return undefined;
	}

	const enteredDate = moment(value, "MM/DD/YYYY");
	const isFuture = enteredDate.isBefore(moment().subtract(1, "years"));
	if (isFuture) {
		return "Date should be not older than 1 year from now";
	}
};

export const dateOfBirth1900 = (value, allValues) => {
	if (value && moment(value, "MM/DD/YYYY").year() < 1900) {
		return "Date should be greater than 01/01/1900";
	}
	return undefined;
};

export const dateOfminYear1990 = (value, allValues) => {
	if (value && moment(value, "MM/DD/YYYY").year() < 1990) {
		return "Date should be greater than 01/01/1990";
	}
	return undefined;
};

export const dateOfBirthMoreThan18 = (value, allValues) => {
	let enteredDate = moment(value, "MM/DD/YYYY");
	if (moment().add(-18, "years").isBefore(enteredDate)) {
		return "Age must be greater than 18 years";
	}
	return undefined;
};

export const validExpDate = (value, allValues) => {
	let enteredDate = moment(value, "MM/YY");

	if (value && (value.length !== 5 || !enteredDate.isValid())) {
		return "Invalid date";
	}

	const nowMinusMonth = moment().add(-1, "M");

	// validation will work only if month is less then current
	if (!nowMinusMonth.isSameOrBefore(enteredDate)) {
		return "Date must be greater than now";
	}

	let dateNoMoreThan10years = moment().add(10, "y").format("YY");
	let enteredYear = moment(enteredDate).format("YY");

	if (enteredYear > dateNoMoreThan10years) {
		return "Date must be not farther than 10 years";
	}

	return undefined;
};

export const addressValidation = [matchesAddressRegex, minLength2, maxLength50];

export const conditionalValidation = (
	condition,
	validators,
	value,
	allValues,
) => {
	const conditionalValidators = condition ? validators : [];

	for (let validator of conditionalValidators) {
		const error = validator(value, allValues);
		if (error) return error;
	}

	return undefined;
};

export const matchesPasswordRegex = matchesRegex(
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).*$/,
	`Must contain at least 1 lowercase and 1 uppercase letters, 1 digit and 1 special character`,
);

export const confirmByName = (name, message) => (value, allValues) => {
	return value === allValues[name] ? null : message;
};

export const passwordConfirmation = confirmByName(
	`password`,
	`Passwords don't match`,
);

export const applyValidatorsIf =
	predicate =>
	(validators = [], elseValidators = []) =>
	(value, allValues, props, fieldName) => {
		return predicate(value, allValues, props, fieldName)
			? validators
					.map(validator => {
						return validator(value, allValues);
					})
					.find(error => !!error)
			: elseValidators
					.map(validator => {
						return validator(value, allValues);
					})
					.find(error => !!error);
	};

export const normalizeAll =
	normalizers => (value, previousValue, allValues, previousAllValues) => {
		let i = 0;
		let normalizersLength = normalizers.length;
		let currentValue = value;
		while (i < normalizersLength) {
			let currentNormalizer = normalizers[i];
			if (typeof currentNormalizer == "function") {
				currentValue = currentNormalizer(
					currentValue,
					previousValue,
					allValues,
					previousAllValues,
				);
			}
			i++;
		}

		return currentValue;
	};

export const conjunctValidators =
	(validators = []) =>
	(value, allValues, props, fieldName) => {
		let err = "";
		for (let validator of validators) {
			err = validator(value, allValues, props, fieldName);
			if (err) return err;
		}
	};

export const exactNumberOfDigitsMask = number => value => {
	if (!value) {
		return value;
	}
	const onlyNums = value.replace(/[^•\d]/g, "");
	return `${onlyNums.slice(0, number)}`;
};

export const exactNumberAndLettersOfDigitsMask = number => value => {
	if (!value) {
		return value;
	}
	const onlyNums = value.replace(/[^a-zA-Z0-9•]/g, "");
	return `${onlyNums.slice(0, number)}`;
};

export const exactNumberAndLettersOfDigitsMask11 =
	exactNumberAndLettersOfDigitsMask(11);

export const exactNumberOfDigitsMask17 = exactNumberOfDigitsMask(17);
export const exactNumberOfDigitsMask50 = exactNumberOfDigitsMask(50);
export const exactNumberOfDigitsMask9 = exactNumberOfDigitsMask(9);
export const exactNumberOfDigitsMask34 = exactNumberOfDigitsMask(34);

export const maskValidators = number => {
	let mask = "";
	for (let i = 0; i < number; i++) {
		mask += "•";
	}

	return mask;
};

export const validateForecastMinCount = (value, formValues) => {
	if (value) {
		formValues.forecastMonthlyDealMinCount = +value;
	}

	if (
		value &&
		(formValues.forecastMonthlyDealMaxCount ||
			formValues.forecastMonthlyDealMaxCount === 0) &&
		+value > +formValues.forecastMonthlyDealMaxCount
	) {
		return "Forecast Monthly Deal Min Count cannot be greater then Forecast Monthly Deal Max Count";
	}
};

export const validateForecastMaxCount = (value, formValues) => {
	if (value) {
		formValues.forecastMonthlyDealMaxCount = +value;
	}

	if (
		value &&
		formValues.forecastMonthlyDealMinCount &&
		+value < +formValues.forecastMonthlyDealMinCount
	) {
		return "Forecast Monthly Deal Max Count cannot be less then Forecast Monthly Deal Min Count";
	}
};

export const matchesTicketDetails = matchesRegex(
	/[\s0-9a-z-!"$%&()+/:;?@\\_']*/i,
	`Only latin letters, digits and special characters ! " $ % & ( ) + - \\ / : ; ? @ _ ' are allowed`,
);

const getProcessorCount = (value, allValues) => {
	return allValues?.processorSpecific?.reduce((acc, item, index) => {
		if (item?.processor === value) {
			return [...acc, index];
		}
		return acc;
	}, []);
};

const getOmahaTypeCount = (count, allValues) => {
	return allValues?.processorSpecific?.reduce((acc, item, index) => {
		if (
			item?.type?.toString() ===
			allValues?.processorSpecific[
				count[count.length - 1]
			].type?.toString()
		) {
			if (
				typeof item?.type === "undefined" ||
				item?.type?.toString() === "0"
			)
				return acc;
			return [...acc, item?.type];
		}
		return acc;
	}, []);
};

const isProcessorRepeated = (count, name) => {
	return (
		count?.length >= 2 &&
		name === `processorSpecific[${count[count.length - 1]}].processor`
	);
};

export const processorHasRepeatedValue = (value, allValues, props, name) => {
	if (value === "other") return undefined;

	const count = getProcessorCount(value, allValues);
	if (isProcessorRepeated(count, name)) {
		if (value === "fiserv_omaha") {
			const countOmaha = getOmahaTypeCount(count, allValues);
			if (countOmaha.length < 2) return undefined;
		}
		return "Processor details already exist for this processor - please update existing processor specific entry or remove it from profile.";
	}
	return undefined;
};

export const briefDescriptionNormalizer = value => {
	if (!value) {
		return value;
	}

	return value.replace(/[%;<>=+~^]+/g, "");
};

export const revenueSourceNameRegex = matchesRegex(
	/^[a-z0-9&!#,.'()_\-+\d\s]*$/i,
	`Allowed characters are: alphanumeric, space and &!#,.'()_-+`,
);

export const validateFile = value => {
	const reservedNames = [
		"CON",
		"PRN",
		"AUX",
		"NUL",
		"COM0",
		"COM1",
		"COM2",
		"COM3",
		"COM4",
		"COM5",
		"COM6",
		"COM7",
		"COM8",
		"COM9",
		"LPT0",
		"LPT1",
		"LPT2",
		"LPT3",
		"LPT4",
		"LPT5",
		"LPT6",
		"LPT7",
		"LPT8",
		"LPT9",
	];

	// Reserved characters
	const reservedCharacters = ["<", ">", ":", '"', "/", "\\", "|", "?", "*"];

	// Checking for reserved names
	const baseName = value.includes(".")
		? value.substring(0, value.indexOf("."))
		: value;
	if (reservedNames.includes(baseName.toUpperCase())) {
		return `You are using a reserved word. Please use a different name.`;
	}

	// Checking for end space or period
	if (value.endsWith(" ") || value.endsWith(".")) {
		return `You can't finish with a space or period.`;
	}

	// Checking for reserved characters
	for (let i = 0; i < reservedCharacters.length; i++) {
		if (value.includes(reservedCharacters[i])) {
			return `You can't use the character ${reservedCharacters[i]}.`;
		}
	}

	// Checking for ASCII NUL character and characters in the range from 1 through 31
	for (let i = 0; i < value.length; i++) {
		const charCode = value.charCodeAt(i);
		if (charCode === 0 || (charCode >= 1 && charCode <= 31)) {
			return `You can't use the character with code ${charCode}.`;
		}
	}

	return undefined;
};

export const totalPercentageOfOwnership = (value, allValues) => {
	const totalOwnership = allValues?.ownerPrincipalSection?.owners.reduce(
		(sum, owner) => sum + parseFloat(owner.ownership || 0),
		0,
	);

	if (totalOwnership > 100) {
		return "Total percentage of ownership for all owners must not exceed 100%";
	}

	return undefined;
};

const {
	SameDay,
	OneThreeDays,
	FourSevenDays,
	EightFourteenDays,
	FifteenThirtyDays,
	ThirtyPlusDays,
} = DaysToDeliveryFieldsNames;

export const validateDaysToDelivery = (value, fields) =>
	riskExposureCalculatorDaysToDeliverySum({
		value,
		fields,
		daysToDeliveryNames: [
			SameDay.name,
			OneThreeDays.name,
			FourSevenDays.name,
			EightFourteenDays.name,
			FifteenThirtyDays.name,
			ThirtyPlusDays.name,
		],
	});

export const validateAddressComponents = (
	addressComponents,
	ignoreStreetNumber = false,
) => {
	const requiredFields = [
		GoogleAddressComponentsFields.Street,
		GoogleAddressComponentsFields.City,
		GoogleAddressComponentsFields.Region,
		GoogleAddressComponentsFields.Country,
		GoogleAddressComponentsFields.PostalCode,
	];
	// We don't have street number field on BE side, so we can't validate this field on edit pages
	if (!ignoreStreetNumber) {
		requiredFields.unshift(GoogleAddressComponentsFields.StreetNumber);
	}

	const missingFields = [];
	const errMessage = "The address is not completed.";

	requiredFields.forEach(field => {
		if (!addressComponents?.[field.name]) {
			missingFields.push(field.displayName);
		}
	});

	if (missingFields.length > 1) {
		return `${errMessage} Missing fields: ${missingFields.join(", ")}.`;
	} else if (missingFields.length === 1) {
		return `${errMessage} Missing field: ${missingFields.join(", ")}.`;
	}
	return null;
};

export const checkAddressComponentsReduxErrors =
	(sectionName, fieldName) => (fieldValue, formValues, formState) => {
		const errors = { ...fp.get(formState.syncErrors, sectionName) };
		delete errors?.[fieldName];
		return checkAddressComponentsErrors(errors);
	};

const checkAddressComponentsErrors = (errors = {}) => {
	let errorMessage = undefined;

	Object.keys(errors).forEach(key => {
		const errorFieldName = Object.values(
			GoogleAddressComponentsFields,
		).find(field => field.name === key)?.displayName;

		errorMessage = errorFieldName
			? `${errorFieldName}'s ${errors[key]?.toLowerCase()}`
			: undefined;
	});

	return errorMessage;
};

export const validateLatLng =
	sectionName =>
	(_value, fields = {}) => {
		if (isEmptyObject(fields) || !fields) return undefined;

		const address = getObjectByPath(fields, sectionName);

		const lat = address?.lat;
		const lng = address?.lng;
		const ownerLat = fields[GoogleAddressOptionFields.Lat];
		const ownerLng = fields[GoogleAddressOptionFields.Lng];

		if (
			!address ||
			checkAddressFieldsEmpty(address) ||
			checkIsLatOrLngManual({
				lat: lat || ownerLat,
				lng: lng || ownerLng,
			})
		)
			return undefined;

		if (sectionName) {
			if (lat && lng) {
				return validateAddressComponents(address, true) || undefined;
			}
		} else {
			if (ownerLat && ownerLng) {
				return validateAddressComponents(fields, true) || undefined;
			}
		}

		return ValidationMessages.AddressNotVerified;
	};
